import moment from "moment";

import * as M from "./model";
import { ZASConstants } from "./model.constants";

export function isIndividual(customer: M.Customer): customer is M.Individual {
    const i = customer as M.Individual;
    return (
        customer &&
        (individualSalutation(customer.Salutation) ||
            (!ZASConstants.SalutationSelectorTable[customer.Salutation] &&
                (!!i.Birthdate || !!i.FirstName || !!i.LastName)))
    );
}

export function isCompany(customer: M.Customer): customer is M.Company {
    const c = customer as M.Company;
    return (
        customer &&
        (companySalutation(customer.Salutation) ||
            (!ZASConstants.SalutationSelectorTable[customer.Salutation] &&
                (!!c.Name || !!c.LegalForm)))
    );
}

export function individualSalutation(salutation: string) {
    if (!ZASConstants.SalutationSelectorTable[salutation]) {
        return false;
    }
    return salutation === "tc_mr" || salutation === "tc_mrs";
}

export function companySalutation(salutation: string) {
    if (!ZASConstants.SalutationSelectorTable[salutation]) {
        return false;
    }
    return !individualSalutation(salutation);
}

export function createConsultationProcess(): M.ConsultationProcess {
    return {
        Customer: createIndividual(),
        Protocol: createProtocol(),
        Agent: createAgent(),
    } as any as M.ConsultationProcess;
}

export function createProtocol(): M.Protocol {
    return {
        CreatorName: undefined,
        ManualAddition: undefined,
        Occasion: "PRODUCT",
        Offers: [createOffer([])],
    } as M.Protocol;
}

export function createAgent(): M.Agent {
    return {
        Address: createAddress(),
    } as M.Agent;
}

export function createIndividual(): M.Individual {
    return {
        "@odata.type": "#com.zurich.zbd.data.model.Individual",
        Salutation: "tc_mr",
        FirstName: null,
        LastName: null,
        Address: createAddress(),
    } as any as M.Individual;
}

export function createCompany(): M.Company {
    return {
        "@odata.type": "#com.zurich.zbd.data.model.Company",
        Salutation: "tc_company",
        Address: createAddress(),
    } as any as M.Company;
}

export function createAddress(): M.Address {
    return {} as M.Address;
}

export function createConsultationDate(): M.ConsultationDate {
    return {
        Date: moment().format("YYYY-MM-DD"), // TODO: must be a date without time
    } as any as M.ConsultationDate;
}

export function createEventOccasionEntry(
    custom: boolean,
    label: string,
): M.OccasionOccasionEntry {
    return {
        OccasionName: label,
        ConsultingRequested: custom,
        ManuallyAdded: custom,
    } as M.OccasionOccasionEntry;
}

export function createGlobalOccasionSection(
    key: string,
    label: string,
    entries: M.GlobalOccasionEntry[],
): M.GlobalOccasionSection {
    return {
        CategoryKey: key,
        CategoryName: label,
        Remark: null,
        GlobalOccasionEntries: entries,
    } as M.GlobalOccasionSection;
}

export function createGlobalOccasionEntry(
    topic: string,
): M.GlobalOccasionEntry {
    return {
        Topic: topic,
        Priority: null,
        ConsultingRequested: null,
        ConsultingDate: null,
    } as M.GlobalOccasionEntry;
}

export function createOffer(divisions: M.Division[] = []): M.Offer {
    return {
        ExternalId: null,
        Divisions: divisions,
        ChangeType: "ADDITION",
    } as M.Offer;
}

export function createDivision(
    application: string,
    offerId: string,
    categoryKey: string,
    categoryName: string,
    lineKey: string,
    lineLabel: string,
    productKey: string,
    productLabel: string,
    productPrintName: string,
    instanceNumber: number,
): M.Division {
    return {
        Id: undefined,
        ExternalId: null,
        Name: productLabel,
        PrintName: productPrintName,
        Label: `${productLabel} ${instanceNumber}`,
        Application: application,
        CategoryKey: categoryKey,
        CategoryName: categoryName,
        ProductKey: productKey,
        InstanceKey: `${productKey}-${offerId}-${instanceNumber}`,
        ChangeType: "ADDITION",
        FinalizationAllowed: true,
        GroupName: lineLabel,
        GroupKey: lineKey,
        Risk: createTrackingText([
            createEvent(application, productKey, "risk"),
        ]),
        Demand: {
            Id: undefined,
            Description: createTrackingText([]),
        },
        Recommendation: {
            Id: undefined,
            Description: createTrackingText([]),
            Reason: createTrackingText([]),
            IsCancellationRecommended: null,
            IsExemptionRecommended: null,
            RecommendedCancellations: [],
            RecommendedExemptions: [],
            CancellationImpacts: createTrackingText([]),
            ExemptionImpacts: createTrackingText([]),
        },
        Decision: {
            Id: undefined,
            Description: createTrackingText([]),
            IsAccepted: null,
            Reason: createTrackingText([]),
            IsDecisionSameAsRecommendation: true,
            AsRecommendedReason: null,
            RejectReason: null,
        },
        ViaLink: false,
        TextblockEnv: null,
        Events: [],
        ProductVersion: null,
    };
}

export function createTrackingText(events: M.Event[] = []): M.TrackingText {
    return {
        Id: undefined,
        IsModified: events.length === 0,
        MainText: null,
        AutomaticText: null,
        AdditionalText: null,
        Events: events,
    };
}

export function createEvent(application, area, field): M.Event {
    return {
        Id: undefined,
        Application: application,
        Area: area,
        Field: field,
        ChangeType: "ADDITION",
        EventName: "",
        OldValue: "", // or null?
        NewValue: "", // or null?
        Payload: "",
        Plaintext: null,
        Sortorder: null,
    };
}

export function createCancellation(): M.Cancellation {
    return {
        Id: undefined,
        CancellationInsuranceNumber: "",
        CancellationCompany: "",
    };
}

export function createExemption(): M.Exemption {
    return {
        Id: undefined,
        ExemptionInsuranceNumber: "",
        ExemptionCompany: "",
    };
}
